import React, { useState } from 'react';
import { ChevronDownIcon, LockIcon, MailIcon, PrinterIcon } from 'lucide-react';
import type { BorrowerPair, DeliveryMethod, SendOptions } from '../../types/disclosures';
import { pluralize } from '../../utils/format';
interface SendConfirmPanelProps {
  pair: BorrowerPair;
  documentCount: number;
  onCancel: () => void;
  onSubmit: (options: SendOptions) => void;
}
export function SendConfirmPanel({
  pair,
  documentCount,
  onCancel,
  onSubmit
}: SendConfirmPanelProps) {
  const [delivery, setDelivery] = useState<DeliveryMethod>('electronic');
  const [notify, setNotify] = useState(true);
  const [sendCopyToTeam, setSendCopyToTeam] = useState(false);
  const [remindAfterTwoDays, setRemindAfterTwoDays] = useState(true);
  const [moreOptions, setMoreOptions] = useState(false);
  const [mailingAddress, setMailingAddress] = useState('123 Main Street, Denver, CO 80202');
  const [fulfillmentType, setFulfillmentType] = useState('Standard Mail (3–5 days)');
  const recipients = pair.recipients;
  const handleSubmit = () => onSubmit({
    recipientIds: recipients.map(recipient => recipient.id),
    delivery,
    notifyImmediately: notify,
    mailingAddress: delivery === 'paper' ? mailingAddress : undefined,
    fulfillmentType: delivery === 'paper' ? fulfillmentType : undefined
  });
  return <>
      <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5" data-id="element-298" data-mp="1nxbn34lprm2r">
        <section data-id="element-299" data-mp="107zh0dlonu4o">
          <h3 className="text-[12px] font-medium uppercase tracking-wide text-slate-500" data-id="element-300" data-mp="107hbsrh1m5km">
            Recipients
          </h3>
          <ul className="mt-2.5 space-y-2" data-id="element-301" data-mp="v60fl99i77ad">
            {recipients.map(recipient => <li key={recipient.id} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3.5 py-3" data-id="element-302" data-mp="19yu6dqu879uc">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white" data-id="element-303" data-mp="hevmyr4jzf9j">
                  <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden="true" data-id="element-304" data-mp="cfizq59rs66f">
                    <path d="M2.5 6.5l2.2 2.2L9.5 3.9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-305" data-mp="2cjcvou4yejbm" />
                  </svg>
                </span>
                <div className="min-w-0 flex-1" data-id="element-306" data-mp="1tla90lzvoygz">
                  <p className="text-[13.5px] font-medium text-slate-900" data-id="element-307" data-mp="1tmlhgz49quef">{recipient.name}</p>
                  <p className="truncate text-[12.5px] text-slate-500" data-id="element-308" data-mp="1yk3r6rq41dnh">{recipient.email}</p>
                </div>
                <span className="shrink-0 text-[12px] text-slate-500" data-id="element-309" data-mp="22njlviimd3xj">{recipient.role}</span>
              </li>)}
          </ul>
          <p className="mt-2 flex items-center gap-1.5 text-[12px] text-slate-500" data-id="element-310" data-mp="3e13v70869pkv">
            <LockIcon className="h-3 w-3" aria-hidden="true" data-id="element-311" data-mp="3izf3h6itjs0z" />
            All borrowers on this pair are required to receive this package.
          </p>
        </section>

        <section data-id="element-312" data-mp="3t5q33avu2v7a">
          <h3 className="text-[12px] font-medium uppercase tracking-wide text-slate-500" data-id="element-313" data-mp="3o4ivpcxlry15">
            Delivery Method
          </h3>
          <div className="mt-2.5 space-y-2" data-id="element-314" data-mp="3jd0z112m4jlz">
            <DeliveryOption icon={MailIcon} selected={delivery === 'electronic'} onSelect={() => setDelivery('electronic')} title="Electronic Delivery" description="Send documents through the borrower signing portal." data-id="element-315" data-mp="3jbprcq61qrpl" />
            <DeliveryOption icon={PrinterIcon} selected={delivery === 'paper'} onSelect={() => setDelivery('paper')} title="Paper / Fulfillment" description="Schedule physical document delivery." data-id="element-316" data-mp="35g44aojdihzt" />
          </div>

          {delivery === 'paper' && <div className="mt-3 space-y-3 rounded-lg border border-slate-200 bg-slate-50/70 p-3.5" data-id="element-317" data-mp="ulgk1mvpsqh3">
              <label className="block" data-id="element-318" data-mp="11oobnw8odoeo">
                <span className="mb-1.5 block text-[12px] font-medium text-slate-600" data-id="element-319" data-mp="11pzi63ti9a9h">
                  Recipient Mailing Address
                </span>
                <input type="text" value={mailingAddress} onChange={event => setMailingAddress(event.target.value)} className="w-full rounded-md border border-slate-200 px-3 py-2 text-[13.5px] text-slate-900 focus:border-slate-400 focus:outline-none" data-id="element-320" data-mp="sp1drkf5n0qd" />
              </label>
              <label className="block" data-id="element-321" data-mp="nr876q9xw4pk">
                <span className="mb-1.5 block text-[12px] font-medium text-slate-600" data-id="element-322" data-mp="91aebx12equ1">
                  Delivery Type
                </span>
                <select value={fulfillmentType} onChange={event => setFulfillmentType(event.target.value)} className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-900 focus:border-slate-400 focus:outline-none" data-id="element-323" data-mp="dzvdru8ht6hf">
                  <option data-id="element-324" data-mp="3cjey84irp4o6">Standard Mail (3–5 days)</option>
                  <option data-id="element-325" data-mp="3ckzrlqtcihkg">Overnight</option>
                  <option data-id="element-326" data-mp="3qj7x4n4g6r78">Courier</option>
                </select>
              </label>
            </div>}
        </section>

        {delivery === 'electronic' && <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50/70 px-3.5 py-3" data-id="element-327" data-mp="3mjpbtq8pse3v">
            <LockIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" data-id="element-328" data-mp="49n7v247lzvi" />
            <div data-id="element-329" data-mp="3vggfooticx90">
              <p className="text-[13px] font-medium text-slate-900" data-id="element-330" data-mp="e1jh99bqxcf1">
                Identity Verification Enabled
              </p>
              <p className="mt-0.5 text-[12.5px] leading-5 text-slate-500" data-id="element-331" data-mp="93re1j5ni3vj">
                Borrowers will be asked to verify their identity before accessing disclosures.
              </p>
            </div>
          </div>}

        <section data-id="element-332" data-mp="i57kxgx9bnv0">
          <h3 className="text-[12px] font-medium uppercase tracking-wide text-slate-500" data-id="element-333" data-mp="i1jw2v2rbpmm">
            Notifications
          </h3>
          <label className="mt-2.5 flex items-center gap-2.5 text-[13.5px] text-slate-800" data-id="element-334" data-mp="3gto0kyxxqssb">
            <input type="checkbox" checked={notify} onChange={event => setNotify(event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900/20" data-id="element-335" data-mp="3kviyrukkkq1v" />
            Notify borrower immediately
          </label>

          <button type="button" onClick={() => setMoreOptions(value => !value)} aria-expanded={moreOptions} className="mt-3 inline-flex items-center gap-1 text-[12.5px] font-medium text-slate-500 transition hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-336" data-mp="3ehz64wamkbb">
            More Options
            <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${moreOptions ? 'rotate-180' : ''}`} data-id="element-337" data-mp="2e6u8q8gfq93k" />
          </button>
          {moreOptions && <div className="mt-2.5 space-y-2.5 rounded-lg border border-slate-200 bg-slate-50/70 p-3.5" data-id="element-338" data-mp="21xmo08dgbx7z">
              <label className="flex items-center gap-2.5 text-[13px] text-slate-800" data-id="element-339" data-mp="1wxs1eh0pltsq">
                <input type="checkbox" checked={sendCopyToTeam} onChange={event => setSendCopyToTeam(event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900/20" data-id="element-340" data-mp="1swydvor6ih0d" />
                Send a copy to the loan team
              </label>
              <label className="flex items-center gap-2.5 text-[13px] text-slate-800" data-id="element-341" data-mp="2godijo6fhaes">
                <input type="checkbox" checked={remindAfterTwoDays} onChange={event => setRemindAfterTwoDays(event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900/20" data-id="element-342" data-mp="2bmwj7x3ldw0b" />
                Remind borrower after 2 days if not signed
              </label>
            </div>}
        </section>
      </div>

      <div className="border-t border-slate-200 bg-slate-50/80 px-6 py-4" data-id="element-343" data-mp="2qcciip3s4vzj">
        <p className="text-[12.5px] text-slate-500" data-id="element-344" data-mp="2qefgpwf35vfc">
          You are sending{' '}
          <span className="font-semibold text-slate-900" data-id="element-345" data-mp="2vbflm4078s4v">
            {pluralize(documentCount, 'document')}
          </span>{' '}
          to{' '}
          <span className="font-semibold text-slate-900" data-id="element-346" data-mp="3o0cwvn5ztoo4">
            {pluralize(recipients.length, 'recipient')}
          </span>{' '}
          via{' '}
          <span className="font-semibold text-slate-900" data-id="element-347" data-mp="398ugvpd4vu4r">
            {delivery === 'electronic' ? 'Electronic Delivery' : 'Paper / Fulfillment'}
          </span>
        </p>
        <div className="mt-3 flex items-center justify-end gap-2" data-id="element-348" data-mp="5snedm6gg2xk">
          <button type="button" onClick={onCancel} className="rounded-md px-3 py-2 text-[13px] font-medium text-slate-600 transition hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-349" data-mp="algfm02pst4w">
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} className="rounded-md bg-slate-900 px-4 py-2.5 text-[13px] font-semibold text-white transition hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2" data-id="element-350" data-mp="243xapa49fb3n">
            Send Initial Disclosures
          </button>
        </div>
      </div>
    </>;
}
interface DeliveryOptionProps {
  icon: React.ComponentType<{
    className?: string;
  }>;
  selected: boolean;
  onSelect: () => void;
  title: string;
  description: string;
}
function DeliveryOption({
  icon: Icon,
  selected,
  onSelect,
  title,
  description
}: DeliveryOptionProps) {
  return <button type="button" onClick={onSelect} aria-pressed={selected} className={`flex w-full items-start gap-3 rounded-lg border px-3.5 py-3 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${selected ? 'border-slate-900 bg-white ring-1 ring-slate-900' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-351" data-mp="2ru1cdbxw10s0">
      <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${selected ? 'border-slate-900' : 'border-slate-300'}`} data-id="element-352" data-mp="jx8a7clgz4dn">
        {selected && <span className="h-2 w-2 rounded-full bg-slate-900" data-id="element-353" data-mp="9y8zloh58yo0" />}
      </span>
      <span className="min-w-0 flex-1" data-id="element-354" data-mp="4zz5zib2i64i">
        <span className="flex items-center gap-2 text-[13.5px] font-medium text-slate-900" data-id="element-355" data-mp="510ou8ybrwws">
          <Icon className="h-4 w-4 text-slate-400" data-id="element-356" data-mp="10yp45c5hdk5" />
          {title}
        </span>
        <span className="mt-0.5 block text-[12.5px] leading-5 text-slate-500" data-id="element-357" data-mp="1zdgrkzrwwsww">{description}</span>
      </span>
    </button>;
}